import Axhelper from '@/lib/Axhelper'

class UserService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			usersFilters: `/api/users/filters`,
			usersSearch: `/api/users/search`,
			userDetails: `/api/users/details`,
			userCreate: `/api/users`,
			userUpdate: `/api/users`,
			userDeleteOne: `/api/users`,
			userDeleteMany: `/api/users/deletemany`,
			userResetPassword: `/api/users/resetpassword`,
		}
	}

	async getAllUsers(options = {}) {
		try {
			const { limit = 100, sortColumn = 'name', sortDirection = 'asc' } = options
			const url = `${this.endpoints.usersFilters}?limit=${limit}&sortcol=${sortColumn}&sortdir=${sortDirection}`
			const response = await Axhelper.sendGetAsync(url)
			if (response.error) return { data: [], error: response.error, totalCount: 0, hasMore: false }
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0
			const hasMore = response.data?.length < totalCount
			return { data: response.data || [], error: null, totalCount, hasMore, headers: response.headers }
		} catch (error) {
			console.error('UserService.getAllUsers error:', error)
			return { data: [], error: error.message || 'Failed to fetch users', totalCount: 0, hasMore: false }
		}
	}

	async searchUsers(searchQuery, options = {}) {
		try {
			if (!searchQuery || searchQuery.length < 3) {
				return { data: [], error: 'Search query must be at least 3 characters long', totalCount: 0, hasMore: false }
			}
			const { limit = 100, sortColumn = 'name', sortDirection = 'asc' } = options
			const url = `${this.endpoints.usersSearch}/${encodeURIComponent(
				searchQuery
			)}?limit=${limit}&sortcol=${sortColumn}&sortdir=${sortDirection}`
			const response = await Axhelper.sendGetAsync(url)
			if (response.error) return { data: [], error: response.error, totalCount: 0, hasMore: false }
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0
			const hasMore = response.data?.length < totalCount
			return { data: response.data || [], error: null, totalCount, hasMore, headers: response.headers }
		} catch (error) {
			console.error('UserService.searchUsers error:', error)
			return { data: [], error: error.message || 'Failed to search users', totalCount: 0, hasMore: false }
		}
	}

	async getUsersWithFilters(options = {}) {
		try {
			const { limit = 50, sortColumn = 'created_at', sortDirection = 'desc', filters = {} } = options
			const url = this._buildFilteredUrl({ limit, sortColumn, sortDirection, filters })
			const response = await Axhelper.sendGetAsync(url)
			if (response.error) return { data: [], error: response.error, totalCount: 0, hasMore: false }
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0
			const hasMore = response.data?.length < totalCount
			return { data: response.data || [], error: null, totalCount, hasMore, headers: response.headers }
		} catch (error) {
			console.error('UserService.getUsersWithFilters error:', error)
			return { data: [], error: error.message || 'Failed to fetch users', totalCount: 0, hasMore: false }
		}
	}

	async getUserById(userId) {
		try {
			if (!userId) return { data: null, error: 'Invalid user ID provided' }
			const url = `${this.endpoints.userDetails}/${encodeURIComponent(userId)}`
			const response = await Axhelper.sendGetAsync(url)
			if (response.error) return { data: null, error: response.error }
			return { data: response.data || null, error: null }
		} catch (error) {
			console.error('UserService.getUserById error:', error)
			return { data: null, error: error.message || 'Failed to fetch user' }
		}
	}

	async createUser(payload) {
		try {
			let body = payload
			if (!(payload instanceof URLSearchParams)) {
				const p = new URLSearchParams()
				if (payload?.name) p.append('name', payload.name)
				if (payload?.initials) p.append('initials', payload.initials)
				if (payload?.email) p.append('email', payload.email)
				if (payload?.password) p.append('password', payload.password)
				body = p
			}
			const res = await Axhelper.sendPostAsync(this.endpoints.userCreate, body)
			if (res?.error) return { success: false, error: res.error, data: null }
			return { success: true, error: null, data: res?.data || null }
		} catch (error) {
			console.error('UserService.createUser error:', error)
			return { success: false, error: error.message || 'Failed to create user', data: null }
		}
	}

	async updateUser(userId, userData) {
		try {
			if (!userId) return { success: false, error: 'Invalid user ID provided' }
			const url = `${this.endpoints.userUpdate}/${encodeURIComponent(userId)}`
			const response = await Axhelper.sendPutAsync(url, userData)
			if (response.error) return { success: false, error: response.error }
			return { success: true, error: null, data: response.data || null }
		} catch (error) {
			console.error('UserService.updateUser error:', error)
			return { success: false, error: error.message || 'Failed to update user' }
		}
	}

	async deleteUsers(userIds) {
		try {
			if (!Array.isArray(userIds) || userIds.length === 0) {
				return { success: false, error: 'Invalid user IDs provided', message: null }
			}
			let ok = false
			if (userIds.length === 1) {
				const url = `${this.endpoints.userDeleteOne}/${userIds[0]}`
				const response = await Axhelper.sendDeleteAsync(url)
				ok = !response?.error
				if (!ok) return { success: false, error: response.error, message: null }
			} else {
				try {
					const bulk = await Axhelper.sendPostAsync(this.endpoints.userDeleteMany, { ids: userIds })
					if (bulk?.error) throw new Error(bulk.error)
					ok = true
				} catch {
					const results = await Promise.allSettled(
						userIds.map(id => Axhelper.sendDeleteAsync(`${this.endpoints.userDeleteOne}/${id}`))
					)
					ok = results.every(r => r.status === 'fulfilled' && !r.value?.error)
					if (!ok) {
						const failed = results
							.map((r, i) => (r.status === 'fulfilled' && !r.value?.error ? null : userIds[i]))
							.filter(Boolean)
						return { success: false, error: `Failed to delete users: ${failed.join(', ')}`, message: null }
					}
				}
			}
			const staticMsg =
				userIds.length > 1 ? 'Selected users have been deleted successfully.' : 'User has been deleted successfully.'
			return { success: true, error: null, message: staticMsg }
		} catch (error) {
			console.error('UserService.deleteUsers error:', error)
			return { success: false, error: error.message || 'Failed to delete users', message: null }
		}
	}

	async resetPassword(userId) {
		try {
			if (!userId) return { success: false, error: 'Invalid user ID provided', message: null }
			const url = `${this.endpoints.userResetPassword}/${encodeURIComponent(userId)}`
			const response = await Axhelper.sendPostAsync(url)
			if (response.error) return { success: false, error: response.error, message: null }
			return { success: true, error: null, message: 'Password reset email sent successfully.' }
		} catch (error) {
			console.error('UserService.resetPassword error:', error)
			return { success: false, error: error.message || 'Failed to reset password', message: null }
		}
	}

	async getUsers(options = {}) {
		const { searchQuery, ...otherOptions } = options
		if (searchQuery && searchQuery.length >= 3) {
			const result = await this.searchUsers(searchQuery, otherOptions)
			return { ...result, isSearching: true }
		}
		const result = await this.getAllUsers(otherOptions)
		return { ...result, isSearching: false }
	}

	validateUser(user) {
		return !!user && typeof user === 'object'
	}
	transformUsers(users) {
		return Array.isArray(users) ? users.map(u => ({ ...u })) : []
	}

	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		let params = [`limit=${limit}`, `sortcol=${sortColumn}`, `sortdir=${sortDirection}`]
		if (filters.searchText && filters.searchText.length >= 3)
			params.push(`searchText=${encodeURIComponent(filters.searchText)}`)
		if (filters.status) params.push(`status=${encodeURIComponent(filters.status)}`)
		if (filters.role) params.push(`role=${encodeURIComponent(filters.role)}`)
		if (filters.fromDate) params.push(`fromDate=${encodeURIComponent(filters.fromDate)}`)
		if (filters.toDate) params.push(`toDate=${encodeURIComponent(filters.toDate)}`)
		return `${this.endpoints.usersFilters}?${params.join('&')}`
	}
}

export default new UserService()
